import { z } from "zod";
import { RACE_LEGS, STATION_IDS } from "./stations.js";

export const PLAN_SCHEMA_VERSION = 1;

export const FormatSchema = z.enum(["singles", "doubles", "relay"]);
export type Format = z.infer<typeof FormatSchema>;

export const CategorySchema = z.enum(["open", "pro"]);
export type Category = z.infer<typeof CategorySchema>;

export const DivisionSchema = z.enum(["men", "women", "mixed"]);
export type Division = z.infer<typeof DivisionSchema>;

/** Number of athletes each format requires. */
export const ATHLETES_PER_FORMAT: Record<Format, number> = {
  singles: 1,
  doubles: 2,
  relay: 4,
};

const seconds = z
  .number()
  .finite()
  .nonnegative()
  .max(24 * 3600);

export const AthleteSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().max(60),
});
export type Athlete = z.infer<typeof AthleteSchema>;

export const RunTargetSchema = z.object({
  paceSecPerKm: z.number().finite().positive().max(3600),
});
export type RunTarget = z.infer<typeof RunTargetSchema>;

export const StationSplitSchema = z.object({
  athleteId: z.string().min(1).max(64),
  pct: z.number().finite().min(0).max(100),
});
export type StationSplit = z.infer<typeof StationSplitSchema>;

export const StationTargetSchema = z.object({
  stationId: z.enum(STATION_IDS),
  /** Target time on the station itself (team time for doubles). */
  targetSec: seconds.positive(),
  /** Roxzone time around this station: run exit to station start plus station end to run start. */
  roxzoneSec: seconds,
  /** Doubles only: share of the station's work done by each athlete, summing to 100. */
  split: z.array(StationSplitSchema).optional(),
});
export type StationTarget = z.infer<typeof StationTargetSchema>;

export const PlanSchema = z
  .object({
    schemaVersion: z.literal(PLAN_SCHEMA_VERSION),
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(100),
    format: FormatSchema,
    category: CategorySchema,
    division: DivisionSchema,
    athletes: z.array(AthleteSchema).min(1).max(4),
    runs: z.array(RunTargetSchema).length(RACE_LEGS),
    stations: z.array(StationTargetSchema).length(RACE_LEGS),
    /** Relay only: time for each of the three handovers between athletes. */
    relayHandoverSec: seconds.optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .superRefine((plan, ctx) => {
    const expected = ATHLETES_PER_FORMAT[plan.format];
    if (plan.athletes.length !== expected) {
      ctx.addIssue({
        code: "custom",
        path: ["athletes"],
        message: `${plan.format} requires ${expected} athlete(s)`,
      });
    }

    const athleteIds = new Set(plan.athletes.map((a) => a.id));
    if (athleteIds.size !== plan.athletes.length) {
      ctx.addIssue({ code: "custom", path: ["athletes"], message: "Athlete ids must be unique" });
    }

    if (plan.format === "singles" && plan.division === "mixed") {
      ctx.addIssue({
        code: "custom",
        path: ["division"],
        message: "Singles has no mixed division",
      });
    }

    plan.stations.forEach((station, i) => {
      if (station.stationId !== STATION_IDS[i]) {
        ctx.addIssue({
          code: "custom",
          path: ["stations", i, "stationId"],
          message: `Expected ${STATION_IDS[i]} at position ${i + 1}`,
        });
      }

      if (station.split === undefined) return;
      if (plan.format !== "doubles") {
        ctx.addIssue({
          code: "custom",
          path: ["stations", i, "split"],
          message: "Station splits only apply to doubles",
        });
        return;
      }
      const splitIds = station.split.map((s) => s.athleteId);
      if (
        new Set(splitIds).size !== splitIds.length ||
        splitIds.some((id) => !athleteIds.has(id))
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["stations", i, "split"],
          message: "Split must reference each plan athlete at most once",
        });
      }
      const total = station.split.reduce((sum, s) => sum + s.pct, 0);
      if (Math.abs(total - 100) > 0.01) {
        ctx.addIssue({
          code: "custom",
          path: ["stations", i, "split"],
          message: `Split must total 100% (got ${total}%)`,
        });
      }
    });
  });
export type Plan = z.infer<typeof PlanSchema>;
